import {
  getRouterLocationQuery,
  locationContainer,
  statusContainer,
  utils,
} from '@wallaby-ui/core';
import {forEach, get, isArray, isEmpty} from 'lodash';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {filterAndSearchSlice} from 'src/js/containers/filter_and_search/filter_and_search_slice';
import {
  changeParamsAndQuery,
  changeQueryParams,
} from 'src/js/containers/filter_and_search/filter_and_search_thunks';
import AutocompleteFetcher from 'src/js/fetchers/autocomplete_fetcher';
import FilterFetcher from 'src/js/fetchers/filter_fetcher';
import HierarchyFetcher from 'src/js/fetchers/hierarchy_fetcher';
import {Filter, FilterParser} from 'src/js/lib/filtering';
import {EMPTY_RESULT_RESPONSE} from 'src/js/model/autocomplete_result';
import {FiltersConfig, FilterSchema} from 'src/js/model/filters_config';

import * as at from './filtering_action_types';
import {getNotEmptySelectedFilters} from './filtering_selectors';
import defaultValuesMock from './mocks/filter_default_values_mock.json';
// import filterConfigMock from 'src/js/model/filters_config_mock.json';

const logger = utils.LogUtils.getLogger('FilteringActions', LIBRARY_NAME);

export function changeFilterParamsAndQuery(
  requestUrl,
  debouncedQuery,
  filteringStateId,
  filterAndSearchStateId,
  paramsPrefix?: string
) {
  return (dispatch, getState) => {
    const state = getState();

    // change filter and reset page
    const params = {
      filter: FilterParser.toString(state[filteringStateId].selectedFilters),
      filterMap: FilterParser.toMap(
        state[filteringStateId].selectedFilters,
        state[filteringStateId].schema
      ),
      page: 1,
    };
    // change component state
    if (debouncedQuery) {
      debouncedQuery(filterAndSearchStateId, requestUrl, params);
    }

    // change location state
    return dispatch(
      locationContainer.changeParams(
        locationContainer.LOCATION_REDUCER_STATE_ID,
        params,
        state[filteringStateId].locationState.reload,
        false,
        paramsPrefix
      )
    );
  };
}

export function focusFilter(stateId, filterId) {
  return (dispatch) =>
    dispatch({type: at.FOCUS_FILTER, payload: filterId, stateId});
}

export function changeFilter(stateId, filterObj) {
  return (dispatch) =>
    dispatch({type: at.UPDATE_FILTER, payload: filterObj, stateId});
}

export function changeFilterAndQuery(
  requestUrl,
  filterObj,
  debouncedQuery,
  filteringStateId,
  filterAndSearchStateId
) {
  return (dispatch) =>
    Promise.all([dispatch(changeFilter(filteringStateId, filterObj))]).then(
      () => {
        dispatch(
          changeFilterParamsAndQuery(
            requestUrl,
            debouncedQuery,
            filteringStateId,
            filterAndSearchStateId
          )
        );
        dispatch(focusFilter(filteringStateId, filterObj.id));
      }
    );
}

export function removeFilter(stateId, filterObj) {
  return (dispatch) =>
    dispatch({type: at.REMOVE_FILTER, payload: filterObj, stateId});
}

export function toggleOpened(stateId, filterName) {
  return (dispatch) =>
    dispatch({type: at.TOGGLE_OPENED, payload: filterName, stateId});
}

export function removeFilterAndQuery(
  requestUrl,
  filterObj,
  debouncedQuery,
  filteringStateId,
  filterAndSearchStateId
) {
  return (dispatch) =>
    Promise.all([dispatch(removeFilter(filteringStateId, filterObj))]).then(
      () => {
        dispatch(
          changeFilterParamsAndQuery(
            requestUrl,
            debouncedQuery,
            filteringStateId,
            filterAndSearchStateId
          )
        );
      }
    );
}

export function removeAllFilters(stateId) {
  return (dispatch) => dispatch({type: at.REMOVE_ALL_FILTERS, stateId});
}

export function loadDefaultFilterValues(stateId) {
  return (dispatch, getState) => {
    const state = getState();
    const {queryFilter} = getRouterLocationQuery(state);
    const selectedFilters = FilterParser.fromString(
      queryFilter,
      state[stateId].schema
    );

    const defaultFilters = state[stateId].schema.getFiltersByView();
    const defaultFilterChanges = [];
    // create filter instances with default value, if they are not already in location query
    forEach(defaultFilters, (filterObj) => {
      if (!selectedFilters.find((el) => el.property === filterObj.property)) {
        const {defaultValue} = state[stateId].filtersData[filterObj.id];
        // do not set undefined default values
        // null values are allowed
        // if (defaultValue || defaultValue === null) {
        const defaultFilterInstance = FilterParser.instantiateFilterWithValue(
          state[stateId].schema,
          filterObj.rawData.name,
          defaultValue || null
        );

        defaultFilterChanges.push(
          dispatch(changeFilter(stateId, defaultFilterInstance))
        );
      }
    });

    // then change query params with updated filters
    return Promise.all(defaultFilterChanges);
  };
}

export function removeAllFiltersAndQuery(
  requestUrl,
  debouncedQuery,
  filteringStateId,
  filterAndSearchStateId,
  paramsPrefix?: string
) {
  return (dispatch) =>
    Promise.all([dispatch(removeAllFilters(filteringStateId))])
      .then(() => dispatch(loadDefaultFilterValues(filteringStateId)))
      .then(() =>
        dispatch(
          changeFilterParamsAndQuery(
            requestUrl,
            debouncedQuery,
            filteringStateId,
            filterAndSearchStateId,
            paramsPrefix
          )
        )
      );
}

export function loadFilters(selectedFilters, stateId) {
  return (dispatch, getState) => {
    const filterChanges = [];
    const oldFilters = getState()[stateId].selectedFilters;
    forEach(oldFilters, (filterObj) => {
      // remove obsolete filters
      if (!Filter.isFilterSelected(selectedFilters, filterObj)) {
        filterChanges.push(dispatch(removeFilter(stateId, filterObj)));
      }
    });
    forEach(selectedFilters, (filterObj) =>
      filterChanges.push(dispatch(changeFilter(stateId, filterObj)))
    );
    return Promise.all(filterChanges);
  };
}

export function resetReload(stateId) {
  return (dispatch) =>
    dispatch({type: at.RESET_RELOAD, payload: false, stateId});
}

export function loadStateFromParams(
  stateId,
  filterAndSearchStateId,
  paramsPrefix?: string
) {
  return (dispatch, getState) => {
    const state = getState();
    const filterQuery = getRouterLocationQuery(state).filter;
    const selectedFilters = FilterParser.fromString(
      filterQuery,
      state[stateId].schema
    );

    return Promise.all([
      dispatch(loadFilters(selectedFilters, stateId)),
      dispatch(
        changeQueryParams(
          filterAndSearchStateId,
          {
            filter: filterQuery,
            filterMap: FilterParser.toMap(
              selectedFilters,
              state[stateId].schema
            ),
          },
          paramsPrefix
        )
      ),
      dispatch(
        filterAndSearchSlice.actions.toggleLoaded(filterAndSearchStateId, {
          filter: false,
        })
      ),
    ]).then(() =>
      Promise.all([
        dispatch({type: at.LOCATION_PARSED, payload: true, stateId}),
        dispatch(resetReload(stateId)),
      ]).then(() =>
        dispatch(
          filterAndSearchSlice.actions.toggleLoaded(filterAndSearchStateId, {
            filter: true,
          })
        )
      )
    );
  };
}

export function loadStateFromParamsWithDefaultValues(
  stateId,
  filterAndSearchStateId,
  userFiltersType,
  paramsPrefix?: string
) {
  return (dispatch, getState) => {
    const state = getState();
    const {filter: queryFilter} = getRouterLocationQuery(state);
    const selectedFilters = FilterParser.fromString(
      queryFilter,
      state[stateId].schema
    );

    // default user filter, if some exists
    const userFilters = state[stateId].userFilters[userFiltersType];
    const defaultUserFilter = userFilters && userFilters.getDefaultFilter();

    // default values
    const defaultFilters = state[stateId].selectedFilters; // initialization
    let newFilters = [...selectedFilters];

    // logic - use default filter, if currently selected filters are empty
    if (defaultUserFilter && newFilters.length === 0) {
      newFilters = FilterParser.fromString(
        defaultUserFilter.getValue(),
        state[stateId].schema
      );
    } else {
      // otherwise, use default filter values
      forEach(defaultFilters, (defaultFilter) => {
        if (
          !selectedFilters.find((el) => el.property === defaultFilter.property)
        ) {
          newFilters.push(defaultFilter);
        }
      });
    }

    const newQueryFilter = FilterParser.toString(newFilters);

    return Promise.all([
      dispatch(loadFilters(newFilters, stateId)),
      dispatch(
        changeQueryParams(
          filterAndSearchStateId,
          {
            filter: newQueryFilter,
            filterMap: FilterParser.toMap(newFilters, state[stateId].schema),
          },
          paramsPrefix
        )
      ),
      dispatch(
        filterAndSearchSlice.actions.toggleLoaded(filterAndSearchStateId, {
          filter: false,
        })
      ),
    ]).then(() =>
      Promise.all([
        dispatch({type: at.LOCATION_PARSED, payload: true, stateId}),
        dispatch(resetReload(stateId)),
      ]).then(() =>
        dispatch(
          filterAndSearchSlice.actions.toggleLoaded(filterAndSearchStateId, {
            filter: true,
          })
        )
      )
    );
  };
}

export function getRangeBounds(stateId, requestUrl, params) {
  return (dispatch) =>
    FilterFetcher.getRange(requestUrl, params)
      .then((response) => {
        dispatch({
          type: at.SCHEMA_RANGE_BOUNDS_FETCH_SUCCESS,
          payload: {params, response},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`SCHEMA_RANGE_BOUNDS_FETCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.SCHEMA_RANGE_BOUNDS_FETCH_FAILURE)
        );
        dispatch({
          type: at.SCHEMA_RANGE_BOUNDS_FETCH_FAILURE,
          payload: error,
          stateId,
        });
      });
}

export function getAllRangeBounds(requestUrl, stateId) {
  return (dispatch, getState) => {
    const state = getState();
    const rangeBounds = [];
    if (state[stateId].schema && state[stateId].schema.getFilters()) {
      state[stateId].schema.getFilters().forEach((filterObj) => {
        if (filterObj.getMin() === null || filterObj.getMax() === null) {
          // fetch range bounds
          rangeBounds.push(
            dispatch(
              getRangeBounds(stateId, requestUrl, {field: filterObj.getName()})
            )
          );
        }
      });
    }
    return Promise.all(rangeBounds);
  };
}

async function resolveFirstItemFromDatasetResult(
  requestUrl: string,
  dataSetId: string
) {
  // TODO: rework when BE is ready
  return defaultValuesMock[dataSetId];
}

export function getDefaultValue(configStateId, requestUrl, filterObj, stateId) {
  return (dispatch) => {
    let promise;
    // first get filter default value
    if (filterObj.getDefaultValue()) {
      promise = Promise.resolve(filterObj.getDefaultValue());
    } else if (filterObj.getDefaultValueDbKey()) {
      if (isArray(filterObj.getDefaultValueDbKey())) {
        // interpolate default value for each array item
        const arrayFilterDefaultValues = [];
        forEach(filterObj.getDefaultValueDbKey(), (defaultValueItem) => {
          arrayFilterDefaultValues.push(
            resolveFirstItemFromDatasetResult(requestUrl, defaultValueItem)
          );
        });
        promise = Promise.all(arrayFilterDefaultValues);
      } else {
        const dataSetId = filterObj.getDefaultValueDbKey();
        promise = resolveFirstItemFromDatasetResult(requestUrl, dataSetId);
      }
    }

    return promise
      .then((result) => {
        dispatch({
          type: at.FILTER_DEFAULT_VALUE_FETCH_SUCCESS,
          payload: {filterId: filterObj.getId(), response: result},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`FILTER_DEFAULT_VALUE_FETCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.FILTER_DEFAULT_VALUE_FETCH_FAILURE)
        );
        dispatch({
          type: at.FILTER_DEFAULT_VALUE_FETCH_FAILURE,
          payload: error,
          stateId,
        });
      });
  };
}

export function getAllDefaultValues(
  configStateId,
  defaultValuesRequestUrl,
  stateId
) {
  return (dispatch, getState) => {
    const state = getState();
    const defaultValues = [];
    if (state[stateId].schema && state[stateId].schema.getFilters()) {
      state[stateId].schema.getFilters().forEach((filterObj) => {
        if (filterObj.getDefaultValue() || filterObj.getDefaultValueDbKey()) {
          // fetch range bounds
          defaultValues.push(
            dispatch(
              getDefaultValue(
                configStateId,
                defaultValuesRequestUrl,
                filterObj,
                stateId
              )
            )
          );
        }
      });
    }
    return Promise.all(defaultValues);
  };
}

export function loadUserFilters(stateId, requestUrl, type) {
  return (dispatch) => {
    // TODO: endpoint is not implemented yet
    // dispatch({type: at.USER_FILTERS_FETCH, payload: {type}, stateId});
    //
    // return FilterFetcher.getUserFilters(requestUrl, type)
    //   .then((response) => {
    //     dispatch({
    //       type: at.USER_FILTERS_FETCH_SUCCESS,
    //       payload: {type, response},
    //       stateId,
    //     });
    //   })
    //   .catch((error) => {
    //     logger.error(`USER_FILTERS_FETCH_FAILURE: ${error}`);
    //     dispatch(
    //       statusContainer.addError(error, at.USER_FILTERS_FETCH_FAILURE)
    //     );
    //     dispatch({
    //       type: at.USER_FILTERS_FETCH_FAILURE,
    //       payload: {error, type},
    //       stateId,
    //     });
    //   });
  };
}

export function setFiltersConfig(
  stateId,
  data,
  rangeBoundsRequestUrl,
  defaultValuesRequestUrl,
  userFiltersRequestUrl,
  userFiltersType,
  filterAndSearchStateId,
  configStateId,
  reload = false
) {
  const filtersConfig = new FiltersConfig(data);
  const schema = new FilterSchema(data);
  return (dispatch) =>
    Promise.all([
      dispatch({
        type: at.SET_FILTERS_CONFIG,
        payload: {filtersConfig, reload, schema},
        stateId,
      }),
    ])
      .then(() => dispatch(getAllRangeBounds(rangeBoundsRequestUrl, stateId)))
      .then(() =>
        dispatch(
          loadUserFilters(stateId, userFiltersRequestUrl, userFiltersType)
        )
      )
      .then(() =>
        dispatch(
          getAllDefaultValues(
            configStateId,
            defaultValuesRequestUrl,
            stateId,
            filtersConfig
          )
        )
      )
      .then(() => dispatch(loadDefaultFilterValues(stateId)))
      .then(() =>
        dispatch(
          loadStateFromParamsWithDefaultValues(
            stateId,
            filterAndSearchStateId,
            userFiltersType
          )
        )
      );
}

export function autocomplete(stateId, requestUrl, params, byPrefixMethod) {
  let promise;
  if (isEmpty(params.prefix)) {
    // request with an empty prefix is not sent
    promise = new Promise((resolve) => {
      resolve(EMPTY_RESULT_RESPONSE);
    });
  } else {
    promise = AutocompleteFetcher.get(requestUrl, params, byPrefixMethod);
  }
  return (dispatch) =>
    promise
      .then((response) => {
        dispatch({
          type: at.AUTOCOMPLETE_FETCH_SUCCESS,
          payload: {params, response},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`AUTOCOMPLETE_FETCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.AUTOCOMPLETE_FETCH_FAILURE)
        );
        dispatch({
          type: at.AUTOCOMPLETE_FETCH_FAILURE,
          payload: error,
          stateId,
        });
      });
}

export function autocompleteGetLabels(
  stateId,
  requestUrl,
  params,
  byPathsMethod
) {
  return (dispatch) =>
    AutocompleteFetcher.getLabels(requestUrl, params, byPathsMethod)
      .then((response) => {
        dispatch({
          type: at.AUTOCOMPLETE_LABELS_FETCH_SUCCESS,
          payload: {params, response},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`AUTOCOMPLETE_LABELS_FETCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.AUTOCOMPLETE_LABELS_FETCH_FAILURE)
        );
        dispatch({
          type: at.AUTOCOMPLETE_LABELS_FETCH_FAILURE,
          payload: error,
          stateId,
        });
      });
}

export function autocompleteSetValues(stateId, hierarchyType, values) {
  return (dispatch) =>
    dispatch({
      type: at.AUTOCOMPLETE_SET_VALUES,
      payload: {hierarchyType, values},
      stateId,
    });
}

export function getHierarchyChildren(stateId, requestUrl, params) {
  return (dispatch) => {
    dispatch({type: at.HIERARCHY_CHILDREN_FETCH, payload: {params}, stateId});

    return HierarchyFetcher.get(requestUrl, params)
      .then((response) => {
        dispatch({
          type: at.HIERARCHY_CHILDREN_FETCH_SUCCESS,
          payload: {params, response},
          stateId,
        });
      })
      .catch((error) => {
        logger.error(`HIERARCHY_CHILDREN_FETCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.HIERARCHY_CHILDREN_FETCH_FAILURE)
        );
        dispatch({
          type: at.HIERARCHY_CHILDREN_FETCH_FAILURE,
          payload: {error, params},
          stateId,
        });
      });
  };
}

export function changeFilterNodeProperty(stateId, id, property, value) {
  return {
    type: at.CHANGE_FILTER_NODE_PROPERTY,
    payload: {id, property, value},
    stateId,
  };
}

export function loadAllDataSets(stateId, requestUrl, datasetIds) {
  return (dispatch) => {
    const datasetPromises = [];
    if (datasetIds) {
      datasetIds.forEach((dataSetId) => {
        datasetPromises.push(
          dispatch(
            configContainer.actions.getDataSet(
              stateId,
              requestUrl,
              dataSetId,
              false
            )
          )
        );
      });
    }
    return Promise.all(datasetPromises);
  };
}

export function loadFiltersConfig(
  stateId,
  filtersConfigPath,
  configStateId,
  rangeBoundsRequestUrl,
  dataSetUrl,
  userFiltersRequestUrl,
  userFiltersType,
  filterAndSearchStateId,
  reload = false
) {
  // TODO-JK: refactor, remove configStateId form parameters
  return async (dispatch, getState, {config}) => {
    const filtersConfig = get(config, filtersConfigPath);

    return dispatch(
      setFiltersConfig(
        stateId,
        filtersConfig,
        rangeBoundsRequestUrl,
        dataSetUrl,
        userFiltersRequestUrl,
        userFiltersType,
        filterAndSearchStateId,
        configStateId,
        reload
      )
    );
    // return dispatch(
    //   loadAllDataSets(configStateId, dataSetUrl, schema.getDataSetIds())
    // );
  };
}

export function saveUserFilter(stateId, requestUrl, type, name) {
  return (dispatch, getState) => {
    const state = getState();
    // not empty filters
    const selectedFilters = getNotEmptySelectedFilters(state, {
      filteringStateId: stateId,
    });
    // get filter value
    const params = {filter: FilterParser.toString(selectedFilters)};
    dispatch({type: at.USER_FILTERS_SAVE, payload: {type, name}, stateId});

    return FilterFetcher.saveUserFilter(requestUrl, type, name, params)
      .then((response) => {
        dispatch({
          type: at.USER_FILTERS_SAVE_SUCCESS,
          payload: {type, response, name},
          stateId,
        });
      })
      .then(() => dispatch(loadUserFilters(stateId, requestUrl, type)))
      .catch((error) => {
        logger.error(`USER_FILTERS_SAVE_FAILURE: ${error}`);
        dispatch(statusContainer.addError(error, at.USER_FILTERS_SAVE_FAILURE));
        dispatch({
          type: at.USER_FILTERS_SAVE_FAILURE,
          payload: {error, type},
          stateId,
        });
      });
  };
}

export function removeUserFilter(stateId, requestUrl, type, id = undefined) {
  return (dispatch) => {
    dispatch({
      type: at.USER_FILTERS_REMOVE,
      payload: {type, name: id},
      stateId,
    });

    return FilterFetcher.deleteUserFilter(requestUrl, id)
      .then((response) => {
        dispatch({
          type: at.USER_FILTERS_REMOVE_SUCCESS,
          payload: {type, response, id},
          stateId,
        });
      })
      .then(() => dispatch(loadUserFilters(stateId, requestUrl, type)))
      .catch((error) => {
        logger.error(`USER_FILTERS_REMOVE_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.USER_FILTERS_REMOVE_FAILURE)
        );
        dispatch({
          type: at.USER_FILTERS_REMOVE_FAILURE,
          payload: {error, type},
          stateId,
        });
      });
  };
}

export function setDefaultUserFilter(stateId, requestUrl, type, id, value) {
  return (dispatch) => {
    dispatch({
      type: at.USER_FILTERS_SET_DEFAULT,
      payload: {type, name: id, default: value},
      stateId,
    });

    return FilterFetcher.setUserFilterDefault(requestUrl, id, value)
      .then((response) => {
        dispatch({
          type: at.USER_FILTERS_SET_DEFAULT_SUCCESS,
          payload: {type, response, id, value},
          stateId,
        });
      })
      .then(() => dispatch(loadUserFilters(stateId, requestUrl, type)))
      .catch((error) => {
        logger.error(`USER_FILTERS_SET_DEFAULT_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(error, at.USER_FILTERS_SET_DEFAULT_FAILURE)
        );
        dispatch({
          type: at.USER_FILTERS_SET_DEFAULT_FAILURE,
          payload: {error, type},
          stateId,
        });
      });
  };
}

export function useUserFilter(
  stateId,
  filterAndSearchStateId,
  requestUrl,
  filterObj
) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedFilters = FilterParser.fromString(
      filterObj,
      state[stateId].schema
    );

    const userFilter = FilterParser.toString(selectedFilters);

    return Promise.all([
      dispatch(loadFilters(selectedFilters, stateId)),
      dispatch(
        changeParamsAndQuery(filterAndSearchStateId, requestUrl, {
          filter: userFilter,
          page: 1,
        })
      ),
    ]);
  };
}
